import { ICertification, ISimulation } from '@/types';

// ================================
// Module Documentation
// ================================

/**
 * # Scoring Navigation Policy
 *
 * Extracted from Phase 3 of the Simulation Score Domain Refactor Plan.
 *
 * ## Purpose
 * Centralizes precondition validation and certification resolution for simulation navigation actions.
 * Previously, this logic was embedded inline in feature hook callbacks with ad hoc branching.
 *
 * ## Responsibilities
 * 1. **Certification Resolution**: Look up certification by ID from the store collection
 * 2. **Action Precondition Validation**: Check required IDs and objects are present
 * 3. **Typed Failure Reporting**: Return explicit failure reasons instead of early returns
 * 4. **Warning Message Mapping**: Convert typed reasons to user-facing console messages
 *
 * ## Key Design Decisions
 * - Uses discriminated union (ok: true | false) for explicit failure handling
 * - Encodes each precondition check as a separate reason type for testability
 * - Returns validated objects on success so callers don't repeat lookup/validation
 * - Warning messages preserve existing hook behavior for backwards compatibility
 *
 * ## Usage Context
 * - Feature hook callbacks delegate navigation preconditions to this policy
 * - Allows cleaner, more testable callback logic without branching noise
 * - Enables domain-level precondition contracts for future navigation flows
 */

// ================================
// Types
// ================================

/**
 * Generic result type for operations that can succeed with typed data or fail with a typed reason.
 *
 * Discriminated union where:
 * - Success: ok: true, plus all properties spread from TSuccess
 * - Failure: ok: false, plus a reason: TFailureReason
 *
 * This pattern allows:
 * - Exhaustive type narrowing via the `ok` discriminant
 * - Type-safe access to success data (TypeScript narrows to success payload)
 * - Type-safe access to failure reasons (TypeScript narrows to reason type)
 * - Minimal boilerplate in usage: no need to unwrap result.data, properties spread naturally
 *
 * @example
 * type MyResult = Result<{ value: string }, 'error-a' | 'error-b'>;
 * // Expands to:
 * // | { ok: true; value: string }
 * // | { ok: false; reason: 'error-a' | 'error-b' }
 */
type Result<TSuccess extends Record<string, unknown>, TFailureReason extends string | number> =
  | ({ ok: true } & TSuccess)
  | { ok: false; reason: TFailureReason };

/**
 * Canonical failure reason union for all simulation navigation actions.
 *
 * Each variant maps 1:1 to a specific precondition violation:
 * - `missing-certification-id`: certificationId param is null/undefined
 * - `missing-next-simulation`: nextSimulation param is null/undefined
 * - `missing-simulation`: simulation param is null/undefined
 * - `missing-simulation-id`: simulationId param is null/undefined
 * - `certification-not-found`: ID was provided but cert not found in store collection
 *
 * This granularity allows callers to:
 * - Log/report specific precondition violations
 * - Distinguish between missing data (should fix upstream) vs lookup failure (store issue)
 * - Centralize warning message logic via getSimulationActionWarningMessage
 */
export type TSimulationActionFailureReason =
  | 'missing-certification-id'
  | 'missing-next-simulation'
  | 'missing-simulation'
  | 'missing-simulation-id'
  | 'certification-not-found';

/**
 * Input contract for certification resolution helpers.
 *
 * Intentionally minimal to avoid coupling helpers to broader hook signatures.
 * Callers pass only what's needed to resolve and validate the certification.
 */
type ResolveCertificationForSimulationActionParams = {
  certificationId?: string | null;
  certifications: ICertification[];
};

/**
 * Input contract for retake role-play precondition validation.
 *
 * Includes all four data elements required to validate a retake attempt:
 * - IDs and objects must be present and correlated
 * - Certification must exist in store collection
 * - Simulation must be present in component prop
 */
type CanRetakeRoleplayParams = {
  certificationId?: string | null;
  certifications: ICertification[];
  simulation?: ISimulation | null;
  simulationId?: string | null;
};

/**
 * Input contract for start-next role-play precondition validation.
 *
 * Subset of retake params because start-next doesn't require current simulation validation.
 * Only needs: certification ID resolution, and a next simulation to navigate to.
 */
type CanStartNextRoleplayParams = {
  certificationId?: string | null;
  certifications: ICertification[];
  nextSimulation?: ISimulation | null;
};

/**
 * Result type for certification resolution with specific failure modes.
 *
 * Uses generic Result type with:
 * - Success: { certification: ICertification }
 * - Failure: Two-case union of missing-id and not-found
 *
 * Expands to discriminated union:
 * - { ok: true; certification: ICertification }
 * - { ok: false; reason: 'missing-certification-id' | 'certification-not-found' }
 */
type CertificationResolutionResult = Result<
  { certification: ICertification },
  Extract<TSimulationActionFailureReason, 'missing-certification-id' | 'certification-not-found'>
>;

/**
 * Result type for retake role-play validation with all possible failure modes.
 *
 * Uses generic Result type with:
 * - Success: { certification: ICertification; simulation: ISimulation }
 * - Failure: Four-case union of all retake precondition failures
 *
 * Expands to discriminated union:
 * - { ok: true; certification: ICertification; simulation: ISimulation }
 * - { ok: false; reason: 'missing-certification-id' | 'missing-simulation-id' | 'missing-simulation' | 'certification-not-found' }
 */
type RetakeRoleplayResolutionResult = Result<
  { certification: ICertification; simulation: ISimulation },
  Extract<
    TSimulationActionFailureReason,
    | 'missing-certification-id'
    | 'missing-simulation-id'
    | 'missing-simulation'
    | 'certification-not-found'
  >
>;

/**
 * Result type for start-next role-play validation with specific failure modes.
 *
 * Uses generic Result type with:
 * - Success: { certification: ICertification; nextSimulation: ISimulation }
 * - Failure: Three-case union of start-next precondition failures
 *
 * Expands to discriminated union:
 * - { ok: true; certification: ICertification; nextSimulation: ISimulation }
 * - { ok: false; reason: 'missing-certification-id' | 'missing-next-simulation' | 'certification-not-found' }
 */
type StartNextRoleplayResolutionResult = Result<
  { certification: ICertification; nextSimulation: ISimulation },
  Extract<
    TSimulationActionFailureReason,
    'missing-certification-id' | 'missing-next-simulation' | 'certification-not-found'
  >
>;

// ================================
// Public Domain Policy
// ================================

/**
 * Resolves certification context required for simulation actions.
 *
 * ## Purpose
 * Validates that a certification ID is provided and looks it up in the store collection.
 * This is a foundational check used by both retake and start-next action validators.
 *
 * ## Preconditions
 * 1. certificationId must be a non-empty string (null/undefined is a failure)
 * 2. certifications array must contain a matching item (uuid field)
 *
 * ## Return Semantics
 * - Success: Returns the resolved certification object (caller can use immediately)
 * - Failure: Specific reason (missing ID or not found) allows appropriate error logging
 *
 * ## Design Notes
 * - Falsy check (`!certificationId`) catches both null and undefined
 * - Empty string '' is also falsy, so no UUID lookups happen on empty IDs
 * - Uses uuid field for matching (not numeric id) to preserve domain semantics
 *
 * ## Example Usage
 * ```ts
 * const result = resolveCertificationForSimulationAction({
 *   certificationId: 'cert-1',
 *   certifications: store.certifications,
 * });
 *
 * if (result.ok) {
 *   // result.certification is guaranteed to exist
 *   return handleAction(result.certification);
 * } else {
 *   console.warn(getSimulationActionWarningMessage(result.reason));
 * }
 * ```
 */
export const resolveCertificationForSimulationAction = ({
  certificationId,
  certifications,
}: ResolveCertificationForSimulationActionParams): CertificationResolutionResult => {
  // First gate: check for missing certification ID (most common case)
  if (!certificationId) {
    return {
      ok: false,
      reason: 'missing-certification-id',
    };
  }

  // Look up certification in store collection by uuid
  const certification = certifications.find((item) => item.uuid === certificationId);

  // Second gate: check if lookup succeeded (indicates store/cache issue if it fails)
  if (!certification) {
    return {
      ok: false,
      reason: 'certification-not-found',
    };
  }

  // Both gates passed; return the resolved certification for immediate use
  return {
    ok: true,
    certification,
  };
};

/**
 * Validates preconditions for retake role-play action.
 *
 * ## Purpose
 * Ensures all four required pieces of data are present and valid before a user can retake
 * a roleplay attempt (i.e., retry the same simulation in the same certification context).
 *
 * ## Precondition Check Order
 * 1. **certificationId present** — Required for context routing
 * 2. **simulationId present** — Required to identify what to retake
 * 3. **certification exists** — Must be in store (delegates to resolveCertificationForSimulationAction)
 * 4. **simulation object present** — The component prop with full scenario details
 *
 * Why order matters:
 * - Early checks (IDs) are cheapest (single falsy check)
 * - Certification resolution is more expensive (array lookup)
 * - Simulation is a component prop already loaded, cheap to check
 *
 * ## Return Semantics
 * - Success: All four preconditions met; caller can immediately invoke retake callback
 * - Failure: Specific reason indicates which precondition failed
 *
 * ## Design Notes
 * - Does NOT validate that simulationId and simulation are correlated
 *   (that's a UI consistency issue, not a navigation precondition)
 * - Delegates certification resolution to reduce duplication
 * - IDs are checked separately because they have different failure reasons
 *
 * ## Example Usage
 * ```ts
 * const resolution = canRetakeRoleplay({
 *   certificationId,
 *   certifications,
 *   simulation,
 *   simulationId,
 * });
 *
 * if (resolution.ok) {
 *   onRetakeRolePlayClick(resolution.certification, resolution.simulation);
 * } else {
 *   console.warn(getSimulationActionWarningMessage(resolution.reason));
 * }
 * ```
 */
export const canRetakeRoleplay = ({
  certificationId,
  certifications,
  simulation,
  simulationId,
}: CanRetakeRoleplayParams): RetakeRoleplayResolutionResult => {
  // Gate 1: certification ID must be present (context for the action)
  if (!certificationId) {
    return {
      ok: false,
      reason: 'missing-certification-id',
    };
  }

  // Gate 2: simulation ID must be present (identifies what to retake)
  if (!simulationId) {
    return {
      ok: false,
      reason: 'missing-simulation-id',
    };
  }

  // Gate 3: resolve certification from store (may fail for two reasons)
  const certificationResolution = resolveCertificationForSimulationAction({
    certificationId,
    certifications,
  });

  // If certification resolution failed, propagate that reason
  if (!certificationResolution.ok) {
    return certificationResolution;
  }

  // Gate 4: simulation object must be present (carries scenario details)
  if (!simulation) {
    return {
      ok: false,
      reason: 'missing-simulation',
    };
  }

  // All gates passed; return validated objects for callback
  return {
    ok: true,
    certification: certificationResolution.certification,
    simulation,
  };
};

/**
 * Validates preconditions for start-next role-play action.
 *
 * ## Purpose
 * Ensures required data is present before navigating to the next roleplay scenario
 * (i.e., user completed current scenario and wants to move forward in the certification path).
 *
 * ## Precondition Check Order
 * 1. **certificationId present** — Required for certification context
 * 2. **nextSimulation present** — Must have a destination to navigate to
 * 3. **certification exists** — Must be in store (delegates to resolveCertificationForSimulationAction)
 *
 * Why order matters:
 * - ID checks are cheapest (single falsy check)
 * - Object check (nextSimulation) is cheap (already in component prop)
 * - Certification lookup is more expensive (array search)
 *
 * ## Return Semantics
 * - Success: All preconditions met; caller can immediately route to next simulation
 * - Failure: Specific reason indicates which precondition failed
 *
 * ## Design Notes
 * - Simpler than canRetakeRoleplay (no simulation ID check) because next-simulation is already
 *   an object passed from parent component
 * - Delegates certification resolution to share logic with retake validator
 *
 * ## Example Usage
 * ```ts
 * const resolution = canStartNextRoleplay({
 *   certificationId,
 *   certifications,
 *   nextSimulation,
 * });
 *
 * if (resolution.ok) {
 *   navigateToSimulation(resolution.certification, resolution.nextSimulation);
 * } else {
 *   console.warn(getSimulationActionWarningMessage(resolution.reason));
 * }
 * ```
 */
export const canStartNextRoleplay = ({
  certificationId,
  certifications,
  nextSimulation,
}: CanStartNextRoleplayParams): StartNextRoleplayResolutionResult => {
  // Gate 1: certification ID must be present (context for routing)
  if (!certificationId) {
    return {
      ok: false,
      reason: 'missing-certification-id',
    };
  }

  // Gate 2: next simulation must be present (cannot navigate without a destination)
  if (!nextSimulation) {
    return {
      ok: false,
      reason: 'missing-next-simulation',
    };
  }

  // Gate 3: resolve certification from store (may fail for two reasons)
  const certificationResolution = resolveCertificationForSimulationAction({
    certificationId,
    certifications,
  });

  // If certification resolution failed, propagate that reason
  if (!certificationResolution.ok) {
    return certificationResolution;
  }

  // All gates passed; return validated objects for routing callback
  return {
    ok: true,
    certification: certificationResolution.certification,
    nextSimulation,
  };
};

/**
 * Maps navigation failure reasons to user-facing warning messages.
 *
 * ## Purpose
 * Centralizes warning message text so:
 * - Messages are consistent across all navigation precondition failures
 * - Changes to message copy happen in one place
 * - Feature hooks remain focused on behavior, not copy
 *
 * ## Message Preservation
 * All strings intentionally match the prior hook implementation to ensure
 * existing logs and error tracking are unaffected by this refactor.
 *
 * ## Dispatch Strategy
 * Uses exhaustive switch to ensure every TSimulationActionFailureReason
 * variant is handled (TypeScript will error if a case is added but not handled).
 *
 * ## Example Usage
 * ```ts
 * const resolution = canRetakeRoleplay(...);
 * if (!resolution.ok) {
 *   console.warn(getSimulationActionWarningMessage(resolution.reason));
 * }
 * ```
 */
export const getSimulationActionWarningMessage = (
  reason: TSimulationActionFailureReason,
): string => {
  switch (reason) {
    case 'missing-certification-id':
      return 'Cannot navigate: missing certificationId';
    case 'missing-next-simulation':
      return 'Cannot navigate: missing nextSimulation';
    case 'missing-simulation':
      return 'Cannot navigate: simulation not found';
    case 'missing-simulation-id':
      return 'Cannot navigate: missing simulationId';
    case 'certification-not-found':
      return 'Cannot navigate: certification not found';
  }
};
